import { FC, useMemo, useState, useEffect, useRef } from 'react';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { AppPage, AppCard } from '../../components/general';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import {
  IntegrationType,
  WaterfallFormModel,
  WaterfallViewModel,
  WaterfallVersionViewModel,
  StepViewModel,
} from '../../models/waterfall';
import FormInputText from '../../components/form/FormInputText';
import FormDropdown from '../../components/form/FormDropdown';
import { classNames } from 'primereact/utils';
import { VersionControls } from './VersionControls';
import { useWaterfallFormSync } from './useWaterfallFormSync';
import { StepEditor } from './StepEditor';
import { createEmptyStep, findAndUpdateStep, findAndDeleteStep, cloneStep } from './stepUtils';

interface WaterfallEditorProps {
  waterfall: WaterfallViewModel | null;
  onSubmit: (data: Partial<WaterfallViewModel>) => Promise<void>;
  onCancel: () => void;
  onCreateVersion?: (sourceVersion: WaterfallVersionViewModel) => void;
  onSetActiveVersion?: (waterfallId: number, waterfallVersionId: number) => void;
  onSaveVersion?: (waterfall: WaterfallVersionViewModel) => void;
  onDeleteVersion?: (versionId: number) => void;
  initialSelectedVersion?: number | null;
}

const integrationTypeOptions = Object.values(IntegrationType).map((value) => ({ label: value, value }));

const validationSchema = yup.object().shape({
  name: yup.string().trim().required('Name is required'),
  integrationType: yup
    .mixed<IntegrationType>()
    .oneOf(Object.values(IntegrationType))
    .required('Integration type is required'),
  selectedVersion: yup.number().nullable(),
});

export const WaterfallEditor: FC<WaterfallEditorProps> = ({
  waterfall,
  onSubmit,
  onCancel,
  onCreateVersion,
  onSetActiveVersion,
  onSaveVersion,
  onDeleteVersion,
  initialSelectedVersion,
}) => {
  const {
    control,
    handleSubmit,
    reset,
    watch,
    getValues,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<WaterfallFormModel>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      name: waterfall?.name ?? '',
      integrationType: waterfall?.integrationType ?? IntegrationType.None,
      selectedVersion: waterfall?.currentVersionId ?? null,
    },
  });

  const waterfallForm = watch();
  const selectedVersion = watch('selectedVersion');

  const versionOptions =
    waterfall?.versions?.map((v) => ({
      label: `v${v.versionNumber}${v.waterfallVersionId === waterfall.currentVersionId ? ' (current version)' : ''}`,
      value: v.waterfallVersionId,
    })) ?? [];

  const handleFormSubmit = async (data: WaterfallFormModel) => {
    // Use ref to get the latest step tree (avoids stale closure issues with async state updates)
    const latestStepTree = localInitialStepRef.current;
    const hasStepDiffLatest = latestStepTree && currentVersion?.initialStep
      ? JSON.stringify(currentVersion.initialStep) !== JSON.stringify(latestStepTree)
      : false;

    
    // Save step tree structure FIRST (includes step details)
    // This ensures data consistency - if step save fails, we don't update waterfall metadata
    if (onSaveVersion && currentVersion && latestStepTree) {
      await onSaveVersion({
        ...currentVersion,
        initialStep: latestStepTree,
      });
    } else {
      console.warn('Not saving version - onSaveVersion:', !!onSaveVersion, 'currentVersion:', !!currentVersion, 'latestStepTree:', !!latestStepTree);
    }

    // Save waterfall metadata AFTER step tree is saved
    // Wait for step tree save to complete before updating metadata
    await onSubmit({
      name: data.name,
      integrationType: waterfall?.integrationType ?? data.integrationType,
    });
  };

  const handleDuplicate = () => {
    if (!onCreateVersion || !waterfall) return;
    const current = waterfall.versions?.find((v) => v.waterfallVersionId === selectedVersion);
    if (current) onCreateVersion(current);
  };

  const handleActivate = () => {
    if (onSetActiveVersion && waterfall && selectedVersion) onSetActiveVersion(waterfall.waterfallId, selectedVersion);
  };

  const handleDelete = () => {
    if (onDeleteVersion && selectedVersion) onDeleteVersion(selectedVersion);
  };

  useWaterfallFormSync({
    waterfall,
    initialSelectedVersion,
    reset,
    getValues,
    setValue,
  });

  const currentVersion = useMemo(() => {
    if (!waterfall || !selectedVersion) return null;
    return waterfall.versions?.find((v) => v.waterfallVersionId === selectedVersion) ?? null;
  }, [waterfall, selectedVersion]);

  // Local state for the step tree (UI only, no backend integration yet)
  const [localInitialStep, setLocalInitialStep] = useState<StepViewModel | null>(null);
  // Ref to track the latest step tree for immediate access (avoids stale closure issues)
  const localInitialStepRef = useRef<StepViewModel | null>(null);

  // Sync local step state with current version
  // Only sync when the version ID changes, not when version data is refetched
  // This prevents resetting local changes when saving
  const previousVersionId = useRef<number | null>(null);
  useEffect(() => {
    const currentVersionId = currentVersion?.waterfallVersionId ?? null;
    
    // Only sync if the version ID actually changed (user selected different version)
    if (currentVersionId !== previousVersionId.current) {
      previousVersionId.current = currentVersionId;
      if (currentVersion?.initialStep) {
        const cloned = cloneStep(currentVersion.initialStep);

        localInitialStepRef.current = cloned; // Update ref immediately
        setLocalInitialStep(cloned);
      } else {
        localInitialStepRef.current = null; // Update ref immediately
        setLocalInitialStep(null);
      }
    } else {
    }
  }, [currentVersion]);

  // Check if there are changes to waterfall metadata
  const hasMetadataDiff =
    waterfall && (waterfall.name !== waterfallForm.name || waterfall.integrationType !== waterfallForm.integrationType);

  // Check if there are changes to the step structure
  const hasStepDiff = useMemo(() => {
    if (!currentVersion || !localInitialStep) return false;
    // Compare step structures by JSON stringifying
    return JSON.stringify(currentVersion.initialStep) !== JSON.stringify(localInitialStep);
  }, [currentVersion, localInitialStep]);

  const hasDiff = hasMetadataDiff || hasStepDiff;

  // For create: disable if name or integrationType is missing
  // For edit: disable if no changes
  const disableFormSave = isSubmitting || 
    (!waterfall && (!waterfallForm.name?.trim() || !waterfallForm.integrationType || waterfallForm.integrationType === IntegrationType.None)) ||
    !!(waterfall && !hasDiff);

  const handleUpdateStep = (updatedStep: StepViewModel) => {
    if (!localInitialStep) return;
    const newStepTree = cloneStep(localInitialStep);

    const found = findAndUpdateStep(newStepTree, updatedStep.stepGuid, (step) => {
      // Preserve nextSteps (tree structure) and stepGuid, but replace all other properties
      const nextSteps = step.nextSteps;
      const stepGuid = step.stepGuid;
      
      // Replace all properties from updatedStep
      Object.assign(step, updatedStep);
      
      // Restore tree structure properties
      step.nextSteps = nextSteps;
      step.stepGuid = stepGuid;
    });

    if (!found) {
      console.warn('Step not found in tree:', updatedStep.stepGuid);
      return;
    }
    localInitialStepRef.current = newStepTree; // Update ref immediately
    setLocalInitialStep(newStepTree); // Update state (async
  };

  const handleAddStep = (parentStep: StepViewModel) => {
    if (!localInitialStep) return;

    const newStep = createEmptyStep();
    const newStepTree = cloneStep(localInitialStep);

    findAndUpdateStep(newStepTree, parentStep.stepGuid, (step) => {
      if (!step.nextSteps) {
        step.nextSteps = [newStep];
      } else {
        step.nextSteps.push(newStep);
      }
    });

    localInitialStepRef.current = newStepTree; // Update ref immediately
    setLocalInitialStep(newStepTree);
  };

  const handleDeleteStep = (stepToDelete: StepViewModel) => {
    if (!localInitialStep) return;

    const newStepTree = cloneStep(localInitialStep);
    const deleted = findAndDeleteStep(newStepTree, stepToDelete.stepGuid);

    if (deleted) {
      localInitialStepRef.current = newStepTree; // Update ref immediately
      setLocalInitialStep(newStepTree);
    }
  };

  return (
    <AppPage title={waterfall ? '' : 'Create Waterfall'} breadcrumbs="Admin - Waterfalls">
      {waterfall && (
        <div className="mb-4 flex items-center gap-2">
          <h1 className="text-2xl font-semibold m-0">Edit Waterfall</h1>
          <Tag value={waterfall.integrationType} severity="info" />
        </div>
      )}
      <AppCard>
        <form onSubmit={handleSubmit(handleFormSubmit)} noValidate className="p-fluid p-input-filled">
          <div className="grid">
            <div className={`col-12 ${waterfall ? 'md:col-8' : 'md:col-6'}`}>
              <FormInputText
                control={control}
                errors={errors}
                field="name"
                label="Name"
                placeholder="Enter Waterfall Name"
                required
              />
            </div>
            {!waterfall && (
              <div className="col-12 md:col-6">
                <FormDropdown
                  control={control}
                  errors={errors}
                  field="integrationType"
                  label="Integration Type"
                  options={integrationTypeOptions}
                  placeholder="Select Type"
                  required
                />
              </div>
            )}
            {waterfall && (
              <div className="col-12 md:col-4">
                <VersionControls
                  waterfall={waterfall}
                  control={control}
                  errors={errors}
                  selectedVersion={selectedVersion}
                  isSubmitting={isSubmitting}
                  versionOptions={versionOptions}
                  onDuplicate={handleDuplicate}
                  onActivate={handleActivate}
                  onDelete={handleDelete}
                />
              </div>
            )}
          </div>

          {waterfall && currentVersion && (
            <div className="mb-5">
              <StepEditor
                initialStep={localInitialStep}
                onUpdateStep={handleUpdateStep}
                onAddStep={handleAddStep}
                onDeleteStep={handleDeleteStep}
                integrationType={waterfall.integrationType}
              />
            </div>
          )}

          <div className="flex justify-end gap-3">
            <Button
              type="button"
              label="Cancel"
              className="p-button-outlined p-button-sm"
              onClick={onCancel}
              disabled={isSubmitting}
            />
            <Button
              className={classNames({ 'opacity-50': disableFormSave }, 'p-button-primary p-button-sm')}
              type="submit"
              label={waterfall ? 'Save' : 'Create'}
              loading={isSubmitting}
              disabled={disableFormSave}
            />
          </div>
        </form>
      </AppCard>
    </AppPage>
  );
};
